import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

// ra-core owns these query parameters for its own lists.
const RESERVED = new Set(["filter", "sort", "order", "page", "perPage"]);
const FLAT_PARAM = /^[a-z][a-zA-Z0-9]*$/;

export const assertVoltParam = (name: string) => {
  if (RESERVED.has(name) || !FLAT_PARAM.test(name)) {
    throw new Error(`Invalid filter parameter "${name}"`);
  }
  return name;
};

export const withVoltParam = (
  params: URLSearchParams,
  name: string,
  value: string | null,
) => {
  const next = new URLSearchParams(params);
  if (value) next.set(assertVoltParam(name), value);
  else next.delete(name);
  return next;
};

export const useVoltFilters = (names: string[]) => {
  names.forEach(assertVoltParam);
  const [params, setParams] = useSearchParams();
  const key = names.join(",");
  const list = useMemo(() => key.split(",").filter(Boolean), [key]);

  const values = useMemo(
    () => Object.fromEntries(list.map((n) => [n, params.get(n) ?? ""])),
    [params, list],
  );
  const activeCount = list.filter((n) => params.get(n)).length;

  const set = useCallback(
    (name: string, value: string | null) =>
      setParams((prev) => withVoltParam(prev, name, value), { replace: true }),
    [setParams],
  );
  const reset = useCallback(
    () =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          list.forEach((n) => next.delete(n));
          return next;
        },
        { replace: true },
      ),
    [setParams, list],
  );

  return { values, activeCount, set, reset };
};
